import { motion } from "framer-motion";
import { useLanguage } from "@/hooks/useLanguage";
import {
  Truck, Zap, Wrench, Hammer, Crosshair, FoldHorizontal,
  Cylinder, Construction, SprayCan, Shield,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

export default function CoreServicesSection() {
  const { t } = useLanguage();

  const services: { icon: LucideIcon; titleKey: string; descKey: string }[] = [
    { icon: Truck, titleKey: "industrialSupply", descKey: "industrialSupplyShort" },
    { icon: Zap, titleKey: "electricalSupply", descKey: "electricalSupplyShort" },
    { icon: Wrench, titleKey: "mechanicalSupply", descKey: "mechanicalSupplyShort" },
    { icon: Hammer, titleKey: "civilMaterials", descKey: "civilMaterialsDesc" },
    { icon: Crosshair, titleKey: "cncLaserCutting", descKey: "cncLaserCuttingDesc" },
    { icon: FoldHorizontal, titleKey: "pressBrakeBending", descKey: "pressBrakeBendingDesc" },
    { icon: Cylinder, titleKey: "tubePipeFab", descKey: "tubePipeFabDesc" },
    { icon: Construction, titleKey: "steelFab", descKey: "steelFabDesc" },
    { icon: SprayCan, titleKey: "powderCoating", descKey: "powderCoatingCapDesc" },
    { icon: Shield, titleKey: "epoxyCoating", descKey: "epoxyCoatingCapDesc" },
  ];

  return (
    <section className="bg-steel-light section-padding px-4 sm:px-6 lg:px-8">
      <div className="content-container">
        <div className="text-center mb-12">
          <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="label-overline text-steel mb-3">
            {t("whatWeDo")}
          </motion.p>
          <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.1 }} className="font-display font-bold text-3xl md:text-4xl text-primary-deep">
            {t("coreServicesTitle")}
          </motion.h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {services.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.titleKey}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.4, delay: i * 0.06, ease: [0.25, 0.46, 0.45, 0.94] }}
                className="group bg-white rounded-xl p-5 border border-muted-border/60 transition-all duration-300 hover:shadow-card-hover hover:-translate-y-1 hover:border-secondary-blue/30 cursor-default"
              >
                <div className="w-11 h-11 rounded-lg bg-primary-deep/5 flex items-center justify-center mb-3 group-hover:bg-primary-deep group-hover:shadow-md transition-all duration-300">
                  <Icon className="w-5 h-5 text-primary-deep group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="font-display font-bold text-sm text-graphite mb-1 leading-tight">{t(s.titleKey)}</h3>
                <p className="text-xs text-steel">{t(s.descKey)}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
